'use no memo';
/**
 * Custom hook for Amplitude event tracking
 *
 * This hook provides a simple interface for tracking custom events
 * throughout the Meetball application.
 *
 * AIDEV-NOTE: "use no memo" directive tells React Compiler to skip this file.
 * This file uses module-level mutable state (earlyEventQueue with .shift()/.push())
 * which is incompatible with React Compiler's automatic memoization.
 */

import { useCallback } from 'react';
import { amplitude, isAmplitudeInitialized } from '@lib/amplitude';
import { ANALYTICS_EVENTS, type MeaningfulActionType } from '@lib/analytics-events';
import {
  captureAttribution,
  getAttribution,
  getOrCreateSessionId,
  touchSession,
} from '@lib/analytics/session';
import { getContextTypeFromPathname } from '@lib/analytics/contextType';
import { normalizeEventName } from '@lib/analytics/normalizeEventName';
import { getShareCopyVariant } from '@lib/analytics/shareCopyVariant';
import { getPlatform } from '@lib/platform';
import { isPwaInstalled } from '@lib/pwa/install-utils';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import { useAuthStore } from '@stores/authStore';
import { defineStoredValue } from '@utils/storedValue';

/** Calendar day (UTC, 'YYYY-MM-DD') a retention event last fired in this tab. */
const retentionFiredDate = defineStoredValue<string>({
  storage: 'session',
  key: 'amplitude_retention_fired_date',
  parse: raw => raw,
});

const scheduleIdle = (cb: () => void, timeoutMs: number) => {
  if (typeof window === 'undefined') {
    setTimeout(cb, 0);
    return;
  }

  const ric = (
    window as unknown as {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    }
  ).requestIdleCallback;

  if (typeof ric === 'function') {
    ric(
      () => {
        cb();
      },
      { timeout: timeoutMs },
    );
  } else {
    setTimeout(cb, 0);
  }
};

// Simple in-memory queue for events fired before initialization or scheduled for idle
interface QueuedEvent {
  name: string;
  props?: EventProperties;
}
const earlyEventQueue: QueuedEvent[] = [];
let flushScheduled = false;

const scheduleFlush = () => {
  if (flushScheduled) return;
  flushScheduled = true;
  const run = () => {
    flushScheduled = false;
    if (!isAmplitudeInitialized()) return; // keep queue until initialized
    while (earlyEventQueue.length) {
      const evt = earlyEventQueue.shift();
      if (!evt) break;
      try {
        amplitude.track(evt.name, evt.props);
      } catch (e) {
        // Swallow individual track errors to avoid blocking others
        logger.error('Failed to flush queued amplitude event', evt.name, e);
      }
    }
  };
  scheduleIdle(run, 2000);
};

type EventProperties = Record<string, unknown>;

const getBasePayload = (): EventProperties => {
  // Get auth state from Zustand (works outside React context via getState())
  const authState = useAuthStore.getState();
  const isAuthenticated = authState.isAuthenticated;
  const userId = authState.user?.id ?? null;

  const sessionId = getOrCreateSessionId();
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const attribution = typeof window !== 'undefined' ? getAttribution() : {};
  const platform = getPlatform();
  return {
    session_id: sessionId,
    path: pathname || null,
    context_type: pathname ? getContextTypeFromPathname(pathname) : 'unknown',
    platform: platform === 'web' ? (isPwaInstalled() ? 'pwa' : 'web') : platform,
    app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : null,
    // Auth state properties for query consistency
    is_authenticated: isAuthenticated,
    user_type: isAuthenticated ? 'authenticated' : 'guest',
    // Stable copy-experiment bucket for share/invite copy (0 when signed out)
    share_copy_variant: getShareCopyVariant(userId),
    // Attribution properties
    utm_source: attribution.utm_source ?? null,
    utm_medium: attribution.utm_medium ?? null,
    utm_campaign: attribution.utm_campaign ?? null,
    utm_content: attribution.utm_content ?? null,
    utm_term: attribution.utm_term ?? null,
    ref: attribution.ref ?? null,
    source: attribution.source ?? null, // Tracks source=app_share, source=qr_[number], etc.
    ref_user: attribution.ref_user ?? null, // User ID who shared (viral loop)
    ref_event: attribution.ref_event ?? null, // Event ID shared (event viral loop)
  };
};

// Test-only: lets unit tests assert the computed base payload without rendering.
export const __getBasePayloadForTest = (): EventProperties => getBasePayload();

/**
 * Hook for tracking events with Amplitude
 * @returns An object with track function and initialization status
 */
export const useAmplitudeTracking = () => {
  const trackEvent = useCallback((eventName: string, properties?: EventProperties) => {
    if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test') return;

    // Analytics MUST never break the user-facing flow. The synchronous
    // prologue below touches storage APIs (touchSession, captureAttribution
    // read/write localStorage; getBasePayload reads the auth store) that
    // can throw in private-browsing / quota-exceeded / sandboxed-iframe
    // contexts. Without this outer try, a thrown helper would propagate
    // into the caller's mutation `onSuccess` and surface as a UI failure
    // on an otherwise-successful join/post.
    //
    // The inner try inside `dispatch` is intentional and complementary:
    // it scopes Amplitude SDK errors to the deferred-dispatch call site
    // so the log line names the specific event that failed to send,
    // rather than getting swallowed by this outer "all of trackEvent".
    try {
      // Refresh session activity + opportunistically capture attribution from the current URL
      touchSession();
      if (typeof window !== 'undefined') {
        captureAttribution(window.location.href);
      }

      const normalizedEventName = normalizeEventName(eventName);
      const payload = {
        ...getBasePayload(),
        ...properties,
        environment: import.meta.env.MODE,
      } as EventProperties;

      // If not initialized yet, enqueue and schedule flush
      if (!isAmplitudeInitialized()) {
        earlyEventQueue.push({ name: normalizedEventName, props: payload });
        scheduleFlush();
        return;
      }

      // Defer actual dispatch to idle to reduce synchronous impact on critical UI paths
      const dispatch = () => {
        try {
          amplitude.track(normalizedEventName, payload);
        } catch (error) {
          logger.error('Failed to track event:', normalizedEventName, sanitizeError(error));
        }
      };

      scheduleIdle(dispatch, 1500);
    } catch (error) {
      // Backstop for the prologue + queueing path. The pre-normalized
      // `eventName` is fine here — if `normalizeEventName` itself was the
      // throw site, we still want a usable identifier in the log.
      logger.error('trackEvent failed:', eventName, sanitizeError(error));
    }
  }, []);

  const trackSessionStarted = useCallback(
    (properties?: EventProperties) => {
      trackEvent(ANALYTICS_EVENTS.session_started, properties);
    },
    [trackEvent],
  );

  const trackMeaningfulAction = useCallback(
    (actionType: MeaningfulActionType, properties?: EventProperties) => {
      trackEvent(ANALYTICS_EVENTS.meaningful_action, {
        action_type: actionType,
        ...properties,
      });
    },
    [trackEvent],
  );

  const setUserProperties = useCallback((properties: EventProperties) => {
    if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test') return;
    if (!isAmplitudeInitialized()) {
      return;
    }

    try {
      const identify = new amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return;
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          identify.set(key, value);
        }
      });
      amplitude.identify(identify);
    } catch (error) {
      logger.error('Failed to set user properties:', error);
    }
  }, []);

  const setUserId = useCallback((userId: string) => {
    if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test') return;
    if (!isAmplitudeInitialized()) {
      return;
    }

    try {
      amplitude.setUserId(userId);
    } catch (error) {
      logger.error('Failed to set user ID:', error);
    }
  }, []);

  const checkRetentionMilestones = useCallback(
    (signupDate: string | null | undefined) => {
      if (import.meta.env.MODE === 'development' || import.meta.env.MODE === 'test') return;
      if (!signupDate) return;
      if (!isAmplitudeInitialized()) return;

      // Parse signup date and calculate days since signup
      const signupTimestamp = new Date(signupDate).getTime();
      const now = Date.now();
      const daysSinceSignup = Math.floor((now - signupTimestamp) / (1000 * 60 * 60 * 24));

      // AIDEV-NOTE: Per-calendar-day deduplication allows retention events to fire on different days
      // for long-lived tabs (e.g., user keeps tab open from day 30 to day 31)
      const lastFiredDate = retentionFiredDate.read();
      const today = new Date().toISOString().split('T')[0]; // Format: 'YYYY-MM-DD'
      if (lastFiredDate === today) return;

      // Fire appropriate retention event based on days since signup
      // AIDEV-NOTE: day_1 and day_7 fire only on exact days; day_30 fires on day 30+ (once per day)
      // This asymmetry allows ongoing long-term retention tracking via days_since_signup property
      let retentionEvent: string | null = null;
      if (daysSinceSignup === 1) {
        retentionEvent = 'day_1_return';
      } else if (daysSinceSignup === 7) {
        retentionEvent = 'day_7_return';
      } else if (daysSinceSignup >= 30) {
        retentionEvent = 'day_30_return';
      }

      if (retentionEvent) {
        trackEvent(retentionEvent, {
          signup_date: signupDate,
          days_since_signup: daysSinceSignup,
        });

        // Mark today's date to prevent duplicate events within same calendar day
        retentionFiredDate.write(today);
      }
    },
    [trackEvent],
  );

  return {
    trackEvent,
    trackSessionStarted,
    trackMeaningfulAction,
    setUserProperties,
    setUserId,
    checkRetentionMilestones,
    isInitialized: isAmplitudeInitialized(),
  };
};
